import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useThemeStore } from '../../../store/ConfigStore';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import { useStore } from '@/store';
import { Table, Input } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import GenericQueryCard, { QueryField } from '@/components/GenericQueryCard';
import { Pagination } from "@/components/Pagination";
import type { DailyDataAnalysis } from '@/types/dataAnalysis';
import { fetchDailyDataAnalysis } from '@/services/dataAnalysis';
import { MobileTableCard } from '@/components/MobileCards';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

export const routeConfig: RouteConfig = {
    title: 'route.dataAnalysis.dailyDataAnalysis',
    layout: true,
    sort: 1
};

const DailyDataAnalysis = () => {
    const { t } = useLocale();
    const toast = useToast();

    // const [selectedProductLine, setSelectedProductLine] = useState('all');
    const [transactionTime, setTransactionTime] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [loading, setLoading] = useState(false);
    const [dailyData, setDailyData] = useState<DailyDataAnalysis[]>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);

    // Helper to build params for client list API
    const buildDataParams = (transactionTime: [Dayjs | null, Dayjs | null]) => {
        let params: any = {};
        if (transactionTime[0] && transactionTime[1]) {
            params.time_range = [transactionTime[0].unix(), transactionTime[1].unix()];
        }
        return params;
    };

    const handleReset = async () => {
        try {
            setTransactionTime([null, null]);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            // fetch new data based on the reset filters
            const res = await fetchDailyDataAnalysis({}, 1, pagination.pageSize);
            setDailyData(res.list);
            setTotal(res.total);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('customerManagement.clientList.fetchError') || 'An error occurred while resetting.'
            });
        }
    };

    const handleQuery = async () => {
        try {
            const params = buildDataParams(transactionTime);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            const res = await fetchDailyDataAnalysis(params, 1, pagination.pageSize);
            setDailyData(res.list);
            setTotal(res.total);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('invitationPromotion.invitationLink.fetchError') || 'An error occurred while querying.'
            });
        }
    };

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('invitationPromotion.invitationLink.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };

    const formatNumber = (num: number | string | null | undefined) => {
        if (num === null || num === undefined) return '--';
        if (isNaN(Number(num))) return '--';
        // 1000000.023024 = 1,000,000.02
        return Number(num).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    };

    const queryFields: QueryField[] = [
        // {
        //     type: 'select',
        //     name: 'productLine',
        //     label: t('dataAnalysis.dailyDataAnalysis.productLine'),
        //     value: selectedProductLine,
        //     onChange: setSelectedProductLine,
        //     options: [
        //         { value: null, label: t('dataAnalysis.dailyDataAnalysis.all') },
        //         { value: 'futures', label: t('dataAnalysis.dailyDataAnalysis.futures') },
        //         { value: 'spot', label: t('dataAnalysis.dailyDataAnalysis.spot') }
        //     ]
        // },
        {
            type: 'dateRange',
            name: 'transactionTime',
            label: t('dataAnalysis.dailyDataAnalysis.transactionTime'),
            value: transactionTime,
            onChange: (dates: [Dayjs | null, Dayjs | null] | null) => {
                setTransactionTime(dates ?? [null, null]);
            },
            placeholder: [t('dataAnalysis.dailyDataAnalysis.startTime'), t('dataAnalysis.dailyDataAnalysis.endTime')],
        }
    ];

    const dailyDataAnalysisColumns: ColumnsType<DailyDataAnalysis> = [
        {
            title: t('dataAnalysis.dailyDataAnalysis.table.date'),
            dataIndex: 'date',
            key: 'date',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text) => formatDate(text)
        },
        {
            title: t('dataAnalysis.dailyDataAnalysis.table.newUsers'),
            dataIndex: 'newUsers',
            key: 'newUsers',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text) => text || '--'
        },
        {
            title: t('dataAnalysis.dailyDataAnalysis.table.activeUsers'),
            dataIndex: 'activeUsers',
            key: 'activeUsers',
            align: 'start',
            width: 130,
            ellipsis: true,
            render: (text) => text || '--'
        },
        {
            title: t('dataAnalysis.dailyDataAnalysis.table.totalDeposits'),
            dataIndex: 'associatedAgent',
            key: 'associatedAgent',
            align: 'start',
            width: 140,
            ellipsis: true,
            render: (text) => formatNumber(text) || '--'
        },
        {
            title: t('dataAnalysis.dailyDataAnalysis.table.totalTradingVolume'),
            dataIndex: 'superiorAgentUID',
            key: 'superiorAgentUID',
            align: 'start',
            width: 140,
            ellipsis: true,
            render: (text) => formatNumber(text) || '--'
        },
        {
            title: t('dataAnalysis.dailyDataAnalysis.table.totalFees'),
            dataIndex: 'tradingPair',
            key: 'tradingPair',
            align: 'start',
            width: 100,
            ellipsis: true,
            render: (text) => formatNumber(text) || '--'
        },
        {
            title: t('dataAnalysis.dailyDataAnalysis.table.totalCommission'),
            dataIndex: 'rebateAmount',
            key: 'rebateAmount',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text) => formatNumber(text) || '--'
        }
    ];

    const mobileDataColumns = dailyDataAnalysisColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    // Fetch data when pagination or filters change
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const params = buildDataParams(transactionTime);
                const res = await fetchDailyDataAnalysis(params, pagination.current, pagination.pageSize);
                setDailyData(res.list);
                setTotal(res.total);
            } catch (error: any) {
                toast.error({
                    message: error?.message || t('dataAnalysis.dailyDataAnalysis.fetchError') || 'An error occurred while fetching data.'
                });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [pagination.current, pagination.pageSize]);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            <GenericQueryCard
                title={t('dataAnalysis.dailyDataAnalysis.title')}
                fields={queryFields}
                onReset={handleReset}
                onQuery={handleQuery}
                gridCols="grid-cols-2 md:grid-cols-4"
            />

            {/* Table Container with Custom Layout */}
            <div className="mt-6 bg-card-background dark:bg-card-background rounded-lg p-6 overflow-hidden">
                {/* Table Content */}
                <div className="hidden md:block h-[556px] overflow-hidden overflow-y-auto overflow-x-auto">
                    <Table
                        dataSource={dailyData}
                        columns={dailyDataAnalysisColumns}
                        rowKey="date"
                        pagination={false}
                        loading={loading}
                        size="small"
                        // scroll={{ x: 850 }}
                        className="custom-table"
                        showHeader={true}
                        locale={{ emptyText: '' }}
                        components={{
                            body: {
                                wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                            }
                        }}
                    />
                </div>
                <div className="block md:hidden">
                    {dailyData.map((row, idx) => (
                        <MobileTableCard
                            key={row.date}
                            record={row}
                            columns={mobileDataColumns}
                            t={t}
                        />
                    ))}
                </div>
                <div className="mt-4 flex justify-end">
                    <Pagination
                        currentPage={pagination.current}
                        totalPages={Math.ceil(total / pagination.pageSize)}
                        onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                        pageSize={pagination.pageSize}
                        setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                        pageSizes={[10, 20, 50, 100]}
                        isPageSizeVisible={true}
                        disabled={loading}
                    />
                </div>
            </div>
        </div>
    );
};

export default DailyDataAnalysis;